import { useEffect, useState } from "react";
import { FieldErrors, useForm, UseFormReset } from "react-hook-form";
import { UseFormReturn } from "react-hook-form/dist/types";
import { UseFormSetValue } from "react-hook-form/dist/types/form";

import { Nullable } from "../../types/common";

import { FormsProps } from "./forms.props";

export interface OtisForm<T> extends UseFormReturn<T> {
  defaultValues: any;
  errors: FieldErrors;
  isDirty: boolean;
  updateDefaultValues: UseFormReset<any>;
}

export const useControlForm = (props: FormsProps): OtisForm<any> => {
  const { focusOnError = false } = props;
  const getContext = () => ({ currentValues: form.getValues(), metaData: props.context });
  const [defaultValues, setDefaultValues] = useState(props.defaultValues);

  const form = useForm({
    defaultValues: props.defaultValues,
    mode: props.mode,
    shouldUnregister: false,
    ...(props.validationRules ? { resolver: props.validationRules } : {}),
    context: { getContext } as object,
  });

  const errors = form.formState.errors;
  const isDirty = Boolean(Object.keys(form.formState.dirtyFields).length);

  const reset: UseFormReset<any> = (values?, keepStateOptions?) => {
    if (!!values) {
      setDefaultValues(values);
    }

    form.reset(values, keepStateOptions);
  };

  const setValue: UseFormSetValue<any> = (name, value, options?) => {
    return form.setValue(name, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
      ...options,
    });
  };

  const updateDefaultValues: UseFormReset<any> = (values, keepStateOptions?) => {
    reset(values, {
      keepDefaultValues: false,
      keepDirty: true,
      keepErrors: true,
      keepIsSubmitted: true,
      keepIsValid: true,
      keepSubmitCount: true,
      keepTouched: true,
      keepValues: true,
      ...keepStateOptions,
    });
  };

  useEffect(() => {
    if (form.formState.isSubmitted && form.formState.isSubmitSuccessful) {
      reset(form.getValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState.isSubmitted, form.formState.isSubmitSuccessful]);

  useEffect(() => {
    if (props.formRef) {
      props.formRef.current = {
        ...props.formRef.current,
        isDirty,
      };
    }
  }, [isDirty, props.formRef]);

  useEffect(() => {
    if (props.formRef) {
      props.formRef.current = {
        ...props.formRef.current,
        handleSubmit: form.handleSubmit,
      };
    }
  }, [form.handleSubmit, props.formRef]);

  useEffect(() => {
    if (focusOnError) {
      const firstError = Object.keys(errors).reduce(
        (field: Nullable<string>, a: Nullable<string>) => {
          return !!errors[field!] ? field : a;
        },
        null
      );

      if (firstError) {
        form.setFocus(firstError);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return {
    ...form,
    defaultValues,
    errors,
    isDirty,
    reset,
    setValue,
    updateDefaultValues,
  };
};
